import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useOrganizationUtilization } from '../../../../../hooks/useOrganizationUtilization';
import { useLoadAccountProductFavorites } from '../../../../../utils/dataLoadingUtils';
import { useRestaurantStore } from '../../../../../stores/appRestaurantStore';
import { useTimeIntervalStore } from '../../../../../stores/appTimeIntervalStore';
import { useFiltersStore } from '../../../../../stores/appFiltersStore';
import { rangeFromTimeControls, fmtBase } from '../../../../../utils/reports/shared';
import {
  EMPTY_UTILIZATION_FILTERS, UtilizationFilters, UtilizationTab,
  applyUtilizationFilters, applyUnmappedFilters, applyUtilizationFiltersToGroups,
  filterRowsByTab, filterGroupedView, getUtilizationStatus,
  isOverOrdered, isUnderOrdered, isRecipeAttributable, potentialWasteForRow,
} from '../../../../../utils/reports/utilizationReport';
import { ALL_RESTAURANTS_ID } from '../../../../../stores/appUserDefaultStore';
import { SlideOverShell, SectionTitle, OverviewCard } from '../../../../ui/slideOverParts';
import { ChevronRight } from 'lucide-react';
import { eur } from '../../../../orderhistory/utils';
import { ReportLoading, EmptyCard, pctAbs } from '../common';
import { useReportHistoryRange } from '../ReportDetailLayout';
import { getCombinedDateRange } from '../../../../../utils/dateRangeFilters';
import UtilizationStatCards, { UnmappedStatCards } from './UtilizationStatCards';
import UtilizationControls from './UtilizationControls';
import OrderedVsNeededTable from './OrderedVsNeededTable';
import PurchasedNotLinkedTable from './PurchasedNotLinkedTable';
import WasteBreakdownCharts from './WasteBreakdownCharts';
import { OrganizationProductDetail } from './OrganizationProductDetail';
import UsedDishesDrawer from './UsedDishesDrawer';
import { resolveWastePercentage } from '../../../../../utils/wasteUtils';
import { ResponsiveTable } from '../../../../shared/mobile/ResponsiveTable';
import { OrganizationUtilizationMobileRows } from './OrganizationUtilizationMobileRows';

/** Organization reporting is read-only apart from its independent favorites list.
 * All recipe edits/setup remain in the individual restaurant view. */
const OrganizationUtilizationReport: React.FC = () => {
  const { t } = useTranslation();
  const view = useTimeIntervalStore(s => s.view);
  const start = useTimeIntervalStore(s => s.startDate);
  const end = useTimeIntervalStore(s => s.endDate);
  const overallType = useFiltersStore(s => s.overallTypeFilter);
  const range = useMemo(() => rangeFromTimeControls(view, start, end), [view, start, end]);
  const { data, reports, inputs, loading, failed, missingSetup, retry } = useOrganizationUtilization(range);
  const publishHistoryRange = useReportHistoryRange();
  useEffect(() => {
    if (loading) return;
    const histories = Object.values(inputs);
    publishHistoryRange?.(getCombinedDateRange(histories.flatMap(input => input.orders), histories.flatMap(input => input.revenue)));
  }, [inputs, loading, publishHistoryRange]);
  const favoritesContext = useLoadAccountProductFavorites(true, 'utilization');
  const reportSettings = useRestaurantStore(s => s.reportSettings);
  const favorites = useRestaurantStore(s => s.accountUtilizationFavorites);
  const toggleFavorite = useRestaurantStore(s => s.toggleAccountFavorite);
  const loadFavorites = useRestaurantStore(s => s.loadAccountFavorites);
  const favoritesCurrent = favorites.context === favoritesContext;
  const favoriteKeys = favoritesCurrent ? favorites.keys : [];
  const favoritesReady = !!favoritesContext && favoritesCurrent && favorites.loaded && !favorites.loading;
  const [pickedTab, setPickedTab] = useState<UtilizationTab | null>(null);
  const tab = pickedTab ?? (favoriteKeys.length ? 'favorites' : 'over');
  const [filters, setFilters] = useState<UtilizationFilters>(EMPTY_UTILIZATION_FILTERS);
  const [drawerKey, setDrawerKey] = useState<string | null>(null);
  const [detailRestaurantId, setDetailRestaurantId] = useState<string | null>(null);
  const [unmappedKey, setUnmappedKey] = useState<string | null>(null);
  const activeSides: Array<'food' | 'drinks'> = overallType === 'food' ? ['food'] : overallType === 'drinks' ? ['drinks'] : ['food', 'drinks'];
  const sideRows = data.rows.filter(r => activeSides.includes(r.side));
  const sideUnmapped = data.unmappedRows.filter(r => activeSides.includes(r.side));
  const filteredRows = applyUtilizationFilters(sideRows, filters);
  const scopedRows = filterRowsByTab(filteredRows, tab, favoriteKeys);
  const unmappedRows = applyUnmappedFilters(sideUnmapped, filters);
  const groups = filterGroupedView(applyUtilizationFiltersToGroups(data.grouped, filters), activeSides, tab, favoriteKeys);
  const pools = filterGroupedView(applyUtilizationFiltersToGroups(data.pooled, filters), activeSides, tab, favoriteKeys);
  const waste = scopedRows.reduce((sum, row) => sum + potentialWasteForRow(row), 0);
  const spend = activeSides.reduce((sum, side) => sum + data.spendBySide[side], 0);
  const unmappedSpend = unmappedRows.reduce((sum, row) => sum + row.spend, 0);
  const breakdown = drawerKey ? data.restaurantBreakdowns.get(drawerKey) ?? [] : [];
  const combinedProduct = data.rows.find(row => row.key === drawerKey);
  const detail = breakdown.find(row => row.restaurantId === detailRestaurantId);
  const localReport = detail ? reports.find(r => r.restaurantId === detail.restaurantId)?.data : undefined;
  const localGroup = detail ? localReport?.pooled.find(g => g.members.some(m => m.key === detail.row.key)) : undefined;
  const periodLabel = `${start.toLocaleDateString()} – ${end.toLocaleDateString()}`;

  if (loading) return <ReportLoading />;

  return (
    <div className="mt-4 sm:mt-6 flex flex-col pb-5">
      <p className="mb-4 text-sm text-muted dark:text-muted-dark">{t('reports.utilization.organization.explainer')}</p>
      {failed.length > 0 && (
        <p role="alert" className="mb-4 p-3 rounded-xl bg-amber-50 dark:bg-amber-950 text-amber-900 dark:text-amber-100">
          {t('reports.utilization.organization.failed', { restaurants: failed.map(r => r.name).join(', ') })}{' '}
          <button type="button" className="underline" onClick={retry}>{t('common.retry')}</button>
        </p>
      )}
      {missingSetup.length > 0 && <p role="status" className="mb-4 text-sm text-muted dark:text-muted-dark">{t('reports.utilization.organization.missingSetup', { restaurants: missingSetup.map(r => r.restaurantName).join(', ') })}</p>}
      {favoritesCurrent && favorites.error && (
        <p role="alert" className="mb-3 text-sm text-muted dark:text-muted-dark">
          {t(`products.favorites.${favorites.error}`)}{' '}
          {favorites.error === 'loadError' && <button type="button" className="underline" onClick={() => void loadFavorites(favoritesContext, 'utilization')}>{t('common.retry')}</button>}
        </p>
      )}
      {reports.length === 0 ? <EmptyCard message={t('reports.utilization.organization.noData')} /> : <>
        {tab === 'unmapped' ? (
          <UnmappedStatCards spend={unmappedSpend} count={unmappedRows.length} shareOfSpend={spend > 0 ? unmappedSpend / spend * 100 : 0} />
        ) : (
          <UtilizationStatCards potentialWasteEur={waste} overOrderingRate={spend > 0 ? waste / spend * 100 : 0}
            productsToReview={scopedRows.filter(r => (data.restaurantBreakdowns.get(r.key) ?? []).some(local => getUtilizationStatus(local.row.utilizationPercent) !== 'healthy')).length}
            unmappedSpend={sideUnmapped.reduce((sum, r) => sum + r.spend, 0)} unmappedCount={sideUnmapped.length} />
        )}
        <UtilizationControls rows={sideRows} unmappedRows={sideUnmapped} tab={tab} onTabChange={setPickedTab}
          counts={{ favorites: filteredRows.filter(r => favoriteKeys.includes(r.key)).length, over: filteredRows.filter(isOverOrdered).length,
            under: filteredRows.filter(isUnderOrdered).length, all: filteredRows.length, recipe: filteredRows.filter(isRecipeAttributable).length, unmapped: unmappedRows.length }}
          filters={filters} onFiltersChange={setFilters} />
        <ResponsiveTable className="mb-6" desktop={<>
          {tab === 'unmapped' ? (
            <PurchasedNotLinkedTable restaurantId={ALL_RESTAURANTS_ID} rows={unmappedRows} totalSpend={unmappedSpend} chartTab={tab}
              onRowClick={row => setUnmappedKey(row.key)} />
          ) : (
            <OrderedVsNeededTable rows={scopedRows} grouped={groups} pooled={pools} favoriteKeys={favoriteKeys}
              favoriteDisabled={key => !favoritesReady || favorites.pending.includes(key)}
              onToggleFavorite={key => void toggleFavorite(favoritesContext, key, 'utilization')}
              onRowClick={row => { setDrawerKey(row.key); setDetailRestaurantId(null); }} chartTab={tab} />
          )}
        </>} mobile={<OrganizationUtilizationMobileRows key={tab} rows={scopedRows} unmappedRows={unmappedRows} unmapped={tab === 'unmapped'}
          favoriteKeys={favoriteKeys} favoriteDisabled={key => !favoritesReady || favorites.pending.includes(key)}
          onToggleFavorite={key => void toggleFavorite(favoritesContext, key, 'utilization')}
          onOpen={key => { if (tab === 'unmapped') setUnmappedKey(key); else { setDrawerKey(key); setDetailRestaurantId(null); } }} />} />
        {tab !== 'unmapped' && <WasteBreakdownCharts rows={scopedRows} />}
      </>}
      {combinedProduct && !detail && (
        <UsedDishesDrawer product={combinedProduct} onClose={() => setDrawerKey(null)}
          periodLabel={`${t('reports.utilization.organization.combinedOverview')} · ${periodLabel}`}
          afterOverview={<section className="mt-6">
            <SectionTitle>{t('reports.utilization.organization.breakdown')}</SectionTitle>
            <p className="mt-1 text-sm text-muted dark:text-muted-dark">{t('reports.utilization.organization.breakdownHint')}</p>
            <div className="mt-3 space-y-3">{breakdown.map(local => (
              <button key={local.restaurantId} type="button" onClick={() => setDetailRestaurantId(local.restaurantId)}
                className="w-full text-left rounded-xl p-4 border border-border dark:border-border-dark bg-background dark:bg-background-dark text-textPrimary dark:text-textPrimary-dark hover:bg-backgroundDarker dark:hover:bg-backgroundDarker-dark">
                <span className="flex items-center justify-between gap-2 font-semibold mb-3">
                  <span className="min-w-0 break-words">{local.restaurantName}</span><ChevronRight className="w-4 h-4 shrink-0" aria-hidden />
                </span>
                <span className="grid grid-cols-2 gap-x-3 gap-y-2 text-sm">
                  <span><span className="block text-muted dark:text-muted-dark">{t('reports.utilization.col.ordered')}</span>{fmtBase(local.row.orderedBase, local.row.stdUnit)}</span>
                  <span><span className="block text-muted dark:text-muted-dark">{t('reports.utilization.col.needed')}</span>{fmtBase(local.row.neededBase, local.row.stdUnit)}</span>
                  <span><span className="block text-muted dark:text-muted-dark">{t('reports.utilization.col.utilization')}</span>{local.row.utilizationPercent === null ? '—' : pctAbs(local.row.utilizationPercent)}</span>
                  <span><span className="block text-muted dark:text-muted-dark">{t('reports.utilization.col.wasteEur')}</span>{local.row.wasteEur === null ? '—' : eur(local.row.wasteEur)}</span>
                </span>
              </button>
            ))}</div>
            <p className="mt-3 text-sm text-muted dark:text-muted-dark">{t('reports.utilization.organization.readOnly')}</p>
          </section>} />
      )}
      {detail && inputs[detail.restaurantId] && <OrganizationProductDetail key={detail.restaurantId} product={detail.row}
        inputs={{ ...inputs[detail.restaurantId], range, defaultWastePercentage: resolveWastePercentage(reportSettings[detail.restaurantId]?.wastePercentage) }} onClose={() => setDetailRestaurantId(null)}
        periodLabel={`${detail.restaurantName} · ${periodLabel}`} groupMembers={localGroup?.members} />}
      {unmappedKey && (
        <SlideOverShell onClose={() => setUnmappedKey(null)} header={t('reports.utilization.organization.breakdown')}>
          <div className="space-y-4 sm:space-y-5">
            <p className="text-sm font-medium text-[#0E8965] dark:text-darkGreenColor-dark">{t('reports.utilization.organization.combinedOverview')} · {periodLabel}</p>
            <h2 className="text-2xl sm:text-[28px] font-bold text-textPrimary dark:text-textPrimary-dark">{data.unmappedRows.find(r => r.key === unmappedKey)?.name}</h2>
            <OverviewCard label={t('reports.utilization.drawer.totalSpend')} value={eur(data.unmappedRows.find(r => r.key === unmappedKey)?.spend ?? 0)} />
            <SectionTitle>{t('reports.utilization.organization.breakdown')}</SectionTitle>
            <p className="text-sm text-muted dark:text-muted-dark">{t('reports.utilization.organization.readOnly')}</p>
            {(data.unmappedBreakdowns.get(unmappedKey) ?? []).map(local => <div key={local.restaurantId} className="rounded-xl p-4 border border-border dark:border-border-dark text-textPrimary dark:text-textPrimary-dark">
              <span className="font-semibold">{local.restaurantName}</span><span className="block">{t('reports.utilization.col.spend')}: {eur(local.row.spend)}</span>
            </div>)}
          </div>
        </SlideOverShell>
      )}
    </div>
  );
};
export default OrganizationUtilizationReport;
